import { OnSelect } from "@lib/utils/types/filters/OnSelect";
import { ToggleAll } from "@lib/utils/types/filters/ToggleAll";
import { processParams } from "@repo/helpers";
import { useRouter, useSearchParams } from "next/navigation";

interface IUseMapForm {
  allowedQueryKeys: string[];
}

export type Query<T extends readonly string[]> = {
  [Key in T[number]]: string[];
};

export function useMapForm({ allowedQueryKeys }: IUseMapForm) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const query: Query<string[]> = {};

  allowedQueryKeys.forEach(key => {
    query[key] = searchParams.getAll(key);
  });

  function clear() {
    const params = processParams({});
    router.push(`?${params.toString()}`, { scroll: false });
  }

  function onSelect({ type, value, name }: OnSelect) {
    function getNewValues() {
      const valuesToUpdate = searchParams.getAll(name);
      if (value === null) return;
      if (type === "single") return [value];
      if (valuesToUpdate.includes(value))
        return valuesToUpdate.filter(v => v !== value);
      return valuesToUpdate.concat(value);
    }

    const newQuery = { ...query };
    delete newQuery.pathId;

    const params = processParams({
      ...newQuery,
      [name]: getNewValues(),
    });
    router.push(`?${params.toString()}`, { scroll: false });
  }

  function toggleAll({ name, type, value }: ToggleAll) {
    function getNewValues() {
      if (type === "add")
        return Array.from(new Set(searchParams.getAll(name).concat(value)));
      return searchParams.getAll(name).filter(v => !value.includes(v));
    }

    const newQuery = { ...query };
    delete newQuery.pathId;

    const params = processParams({
      ...newQuery,
      [name]: getNewValues(),
    });
    router.push(`?${params.toString()}`, { scroll: false });
  }

  return [query, onSelect, toggleAll, clear] as const;
}
